import React, { useEffect, useState } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import PublicNav from "@/components/PublicNav";
import PublicFooter from "@/components/PublicFooter";
import usePageMeta from "@/hooks/usePageMeta";
import useBranding from "@/hooks/useBranding";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { MapPin, ShieldCheck, Phone, Mail, Globe, Calendar, Ruler, GraduationCap, FileText, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

export default function SchoolDetail({ overrideId }) {
  const params = useParams();
  const id = overrideId || params.id || params.param;
  const [s, setS] = useState(null);
  const [open, setOpen] = useState(false);
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState({ parent_name: "", parent_email: "", parent_phone: "", student_name: "", student_class: "", message: "" });

  useEffect(() => {
    if (!id || id.startsWith("in-")) return;
    setS(null);
    setOpen(false);
    setSent(false);
    api.get(`/schools/${id}`).then((r) => setS(r.data));
  }, [id]);

  const [searchParams] = useSearchParams();
  useEffect(() => {
    if (s && searchParams.get("enquire") === "1") {
      setOpen(true);
      setSent(false);
    }
  }, [s, searchParams]);

  const brand = useBranding();
  const brandSuffix = brand?.site_title ? ` — ${brand.site_title}` : "";
  const metaTitle = s
    ? (s.seo_title || `${s.name}, ${s.city}${brandSuffix}`)
    : `Loading${brandSuffix}`;
  const metaDesc = s
    ? (s.meta_description || `${s.name} in ${s.city}, ${s.state}. Board: ${s.board || "—"}. View facilities, fees, admissions and enquire directly.`)
    : "";
  usePageMeta(metaTitle, metaDesc);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/enquiries", { school_id: s.id, ...f });
      setSent(true);
      setF({ parent_name: "", parent_email: "", parent_phone: "", student_name: "", student_class: "", message: "" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to submit");
    } finally { setBusy(false); }
  };

  const openDialog = () => { setSent(false); setOpen(true); };

  if (!s) return <div className="min-h-screen bg-white"><PublicNav /><div className="p-10 text-slate-500">Loading…</div></div>;

  return (
    <div className="min-h-screen bg-white">
      <PublicNav />
      {/* Hero image */}
      <div className="relative h-72 md:h-96 bg-slate-100">
        <img src={s.feature_image_url} alt={s.name} className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-900/70 via-slate-900/30 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 mx-auto max-w-7xl px-6 pb-8 text-white">
          <div className="flex flex-wrap items-center gap-2 mb-3">
            {s.featured && <Badge className="bg-amber-400 text-slate-900 hover:bg-amber-400">Featured</Badge>}
            {s.verified && <Badge className="bg-emerald-600 hover:bg-emerald-600"><ShieldCheck className="w-3 h-3 mr-1" />Verified by School</Badge>}
            {s.admission_open ? (
              <Badge className="bg-blue-600 hover:bg-blue-600" data-testid="admission-open-badge">Admission Open</Badge>
            ) : (
              <Badge variant="outline" className="border-white/40 text-white">Admission Closed</Badge>
            )}
          </div>
          <h1 className="text-3xl md:text-5xl font-black tracking-tight" style={{ textShadow: "0 2px 20px rgba(0,0,0,0.4)" }} data-testid="school-detail-name">{s.name}</h1>
          <div className="mt-2 text-slate-200 flex items-center gap-2"><MapPin className="w-4 h-4" /> {s.address || `${s.city}, ${s.state}`}</div>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-6 py-10 grid lg:grid-cols-3 gap-10">
        <div className="lg:col-span-2 space-y-10">
          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">About</h2>
            <p className="text-slate-600 leading-relaxed">{s.description}</p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">Facilities</h2>
            <div className="flex flex-wrap gap-2">
              {(s.facilities || []).map((f, i) => (
                <Badge key={i} variant="outline" className="border-slate-300 text-slate-700 py-1.5 px-3">{f}</Badge>
              ))}
            </div>
          </section>

          {s.gallery?.length > 0 && (
            <section>
              <h2 className="text-2xl font-bold text-slate-900 mb-4">Gallery</h2>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {s.gallery.map((g, i) => (
                  <div key={i} className="aspect-[4/3] rounded overflow-hidden bg-slate-100" data-testid={`gallery-img-${i}`}>
                    <img src={g} alt="" className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" />
                  </div>
                ))}
              </div>
            </section>
          )}

          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">Fees Structure</h2>
            <div className="bg-slate-50 rounded-lg p-6 border border-slate-200">
              <div className="text-sm text-slate-600 whitespace-pre-wrap">{s.fees_structure || "Contact school for details"}</div>
              <div className="mt-4 text-2xl font-bold text-slate-900">₹{(s.fees_min || 0).toLocaleString()} - ₹{(s.fees_max || 0).toLocaleString()}<span className="text-sm font-normal text-slate-500"> / year</span></div>
            </div>
          </section>

          {s.brochures?.length > 0 && (
            <section>
              <h2 className="text-2xl font-bold text-slate-900 mb-4">Brochures</h2>
              <div className="space-y-2">
                {s.brochures.map((b, i) => (
                  <a key={i} href={b} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-blue-700 hover:underline">
                    <FileText className="w-4 h-4" /> Download brochure {i + 1}
                  </a>
                ))}
              </div>
            </section>
          )}
        </div>

        {/* Sidebar */}
        <aside className="space-y-6">
          <div className="bg-white border border-slate-200 rounded-lg p-6 sticky top-24">
            <h3 className="font-bold text-slate-900 text-lg mb-4">Quick Facts</h3>
            <ul className="space-y-3 text-sm">
              <li className="flex items-center gap-3 text-slate-600"><GraduationCap className="w-4 h-4 text-slate-400" /><span className="font-semibold text-slate-900">{s.board}</span> · {s.school_type}</li>
              <li className="flex items-center gap-3 text-slate-600"><Calendar className="w-4 h-4 text-slate-400" />Established {s.establishment_year}</li>
              <li className="flex items-center gap-3 text-slate-600"><Ruler className="w-4 h-4 text-slate-400" />{s.size_acres} acres campus</li>
              {s.contact_phone && <li className="flex items-center gap-3 text-slate-600"><Phone className="w-4 h-4 text-slate-400" />{s.contact_phone}</li>}
              {s.contact_email && <li className="flex items-center gap-3 text-slate-600"><Mail className="w-4 h-4 text-slate-400" />{s.contact_email}</li>}
              {s.website && <li className="flex items-center gap-3 text-slate-600"><Globe className="w-4 h-4 text-slate-400" /><a href={s.website} target="_blank" rel="noopener noreferrer" className="text-blue-700 hover:underline">{s.website}</a></li>}
            </ul>
            <Button className="w-full mt-6 bg-slate-900 hover:bg-slate-800" onClick={openDialog} data-testid="enquire-btn">Enquire Now</Button>
            <Link to="/schools"><Button variant="outline" className="w-full mt-2">← Back to listing</Button></Link>
          </div>
        </aside>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{sent ? "Enquiry received" : `Enquire about ${s.name}`}</DialogTitle>
            {!sent && (
              <DialogDescription>
                Fill your details — the school's admissions team will contact you back within 1-2 business days.
              </DialogDescription>
            )}
          </DialogHeader>
          {sent ? (
            <div className="text-center py-6" data-testid="enquiry-success">
              <CheckCircle2 className="w-14 h-14 mx-auto text-emerald-500" />
              <p className="text-slate-700 mt-4 font-semibold">Thanks! Your enquiry has been sent to {s.name}.</p>
              <p className="text-slate-500 text-sm mt-1">They will reach out to you soon.</p>
              <Button className="mt-6 bg-slate-900 hover:bg-slate-800" onClick={() => setOpen(false)} data-testid="enquiry-close">Close</Button>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-3" data-testid="enquiry-form">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="mb-1 block text-xs">Parent Name *</Label>
                  <Input required value={f.parent_name} onChange={(e) => setF({ ...f, parent_name: e.target.value })} data-testid="enq-parent-name" />
                </div>
                <div>
                  <Label className="mb-1 block text-xs">Phone *</Label>
                  <Input required type="tel" value={f.parent_phone} onChange={(e) => setF({ ...f, parent_phone: e.target.value })} data-testid="enq-parent-phone" />
                </div>
              </div>
              <div>
                <Label className="mb-1 block text-xs">Email *</Label>
                <Input required type="email" value={f.parent_email} onChange={(e) => setF({ ...f, parent_email: e.target.value })} data-testid="enq-parent-email" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="mb-1 block text-xs">Student Name</Label>
                  <Input value={f.student_name} onChange={(e) => setF({ ...f, student_name: e.target.value })} data-testid="enq-student-name" />
                </div>
                <div>
                  <Label className="mb-1 block text-xs">Applying for Class</Label>
                  <Input placeholder="e.g. 5" value={f.student_class} onChange={(e) => setF({ ...f, student_class: e.target.value })} data-testid="enq-student-class" />
                </div>
              </div>
              <div>
                <Label className="mb-1 block text-xs">Message</Label>
                <Textarea rows={3} placeholder="Any specific questions?" value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })} data-testid="enq-message" />
              </div>
              <DialogFooter>
                <Button type="submit" disabled={busy} className="bg-slate-900 hover:bg-slate-800" data-testid="enq-submit">
                  {busy ? "Sending…" : "Send Enquiry"}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      <PublicFooter />
    </div>
  );
}
